import { useCallback, useEffect, useState } from 'react'
import { Users, Plus, CheckCircle, XCircle, RefreshCw, X, Shield } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import type { AdminUser } from '../types'
import { formatDate } from '../utils/helpers'
import PhoneInput from '../components/PhoneInput'

interface InviteForm {
  email: string
  firstname: string
  lastname: string
  phone: string
  customer_role: 'user' | 'admin'
}

const emptyForm: InviteForm = {
  email: '',
  firstname: '',
  lastname: '',
  phone: '',
  customer_role: 'user',
}

export default function MyTeam() {
  const { t } = useTranslation()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<InviteForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<number | null>(null)
  const [roleConfirm, setRoleConfirm] = useState<{
    user: AdminUser
    newRole: 'user' | 'admin'
  } | null>(null)
  const [roleError, setRoleError] = useState<string | null>(null)

  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.getCustomerUsers()
      setUsers(data)
      setError(null)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setError(apiErr?.response?.data?.error || t('myTeam.loadError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.email) {
      setFormError(t('myTeam.emailRequired'))
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      const created = await api.createCustomerUser(form)
      setUsers((prev) => [...prev, created])
      setShowModal(false)
      setForm(emptyForm)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setFormError(apiErr?.response?.data?.error || t('myTeam.inviteError'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (user: AdminUser) => {
    setTogglingId(user.id)
    try {
      const updated = await api.updateCustomerUser(user.id, { is_active: !user.is_active })
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)))
    } catch {
      alert(t('myTeam.toggleError'))
    } finally {
      setTogglingId(null)
    }
  }

  const handleRoleChange = async (user: AdminUser, newRole: 'user' | 'admin') => {
    // On retire le rôle admin → vérifications nécessaires
    if (user.customer_role === 'admin' && newRole === 'user') {
      const otherAdmins = users.filter((u) => u.customer_role === 'admin' && u.id !== user.id)
      if (otherAdmins.length === 0) {
        // Dernier admin → on bloque
        setRoleError(
          t('myTeam.lastAdminError', {
            user: [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email,
          })
        )
        return
      }
      // Pas le dernier → on demande confirmation
      setRoleConfirm({ user, newRole })
      return
    }
    await doRoleChange(user, newRole)
  }

  const doRoleChange = async (user: AdminUser, newRole: 'user' | 'admin') => {
    try {
      const updated = await api.updateCustomerUser(user.id, { customer_role: newRole })
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)))
    } catch {
      alert(t('myTeam.updateError'))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="h-6 w-6 text-blue-600" />
            {t('myTeam.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('myTeam.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadUsers}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
            title={t('common.refresh', 'Rafraîchir')}
          >
            <RefreshCw size={18} />
          </button>
          <button
            onClick={() => {
              setShowModal(true)
              setForm(emptyForm)
              setFormError(null)
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
          >
            <Plus size={16} />
            {t('myTeam.inviteUser')}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Users table */}
      {users.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <Users className="mx-auto h-12 w-12 mb-4 opacity-40" />
          <p className="text-lg">{t('myTeam.noUsers')}</p>
        </div>
      ) : (
        <>
          {/* Mobile card layout */}
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 sm:hidden bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
            {users.map((u) => (
              <li key={u.id} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {[u.firstname, u.lastname].filter(Boolean).join(' ') || '—'}
                    </p>
                    {u.phone && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{u.phone}</p>
                    )}
                    <p className="text-sm text-gray-600 dark:text-gray-300">{u.email}</p>
                  </div>
                  {u.is_active ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 flex-shrink-0">
                      <CheckCircle size={12} />
                      {t('myTeam.statusActive')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 flex-shrink-0">
                      <XCircle size={12} />
                      {t('myTeam.statusInactive')}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <select
                    value={u.customer_role ?? 'user'}
                    onChange={(e) => handleRoleChange(u, e.target.value as 'user' | 'admin')}
                    className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    <option value="user">{t('myTeam.roleUser')}</option>
                    <option value="admin">{t('myTeam.roleAdmin')}</option>
                  </select>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {u.last_login ? formatDate(u.last_login) : '—'}
                  </span>
                </div>
                <button
                  onClick={() => handleToggleActive(u)}
                  disabled={togglingId === u.id}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                    u.is_active
                      ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400'
                      : 'bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400'
                  }`}
                >
                  {togglingId === u.id ? (
                    <RefreshCw size={12} className="animate-spin" />
                  ) : u.is_active ? (
                    t('myTeam.deactivate')
                  ) : (
                    t('myTeam.activate')
                  )}
                </button>
              </li>
            ))}
          </ul>

          {/* Desktop table layout */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  {[
                    'myTeam.colName',
                    'myTeam.colEmail',
                    'myTeam.colRole',
                    'myTeam.colStatus',
                    'myTeam.colLastLogin',
                    'myTeam.colActions',
                  ].map((key) => (
                    <th
                      key={key}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      {t(key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {[u.firstname, u.lastname].filter(Boolean).join(' ') || '—'}
                      </div>
                      {u.phone && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{u.phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {u.email}
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={u.customer_role ?? 'user'}
                        onChange={(e) => handleRoleChange(u, e.target.value as 'user' | 'admin')}
                        className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                      >
                        <option value="user">{t('myTeam.roleUser')}</option>
                        <option value="admin">{t('myTeam.roleAdmin')}</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      {u.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          <CheckCircle size={12} />
                          {t('myTeam.statusActive')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                          <XCircle size={12} />
                          {t('myTeam.statusInactive')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {u.last_login ? formatDate(u.last_login) : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleActive(u)}
                        disabled={togglingId === u.id}
                        className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                          u.is_active
                            ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400'
                            : 'bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400'
                        }`}
                      >
                        {togglingId === u.id ? (
                          <RefreshCw size={12} className="animate-spin" />
                        ) : u.is_active ? (
                          t('myTeam.deactivate')
                        ) : (
                          t('myTeam.activate')
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Invite modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Shield size={18} className="text-blue-600" />
                {t('myTeam.inviteUser')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleInvite} className="p-6 space-y-4">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                  {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('myTeam.colEmail')} *
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('myTeam.firstname')}
                  </label>
                  <input
                    type="text"
                    value={form.firstname}
                    onChange={(e) => setForm((f) => ({ ...f, firstname: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('myTeam.lastname')}
                  </label>
                  <input
                    type="text"
                    value={form.lastname}
                    onChange={(e) => setForm((f) => ({ ...f, lastname: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <PhoneInput
                value={form.phone}
                onChange={(phone) => setForm((f: InviteForm) => ({ ...f, phone }))}
                label={t('myTeam.phone')}
                labelCls="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                fieldCls="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('myTeam.colRole')}
                </label>
                <select
                  value={form.customer_role}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, customer_role: e.target.value as 'user' | 'admin' }))
                  }
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="user">{t('myTeam.roleUser')}</option>
                  <option value="admin">{t('myTeam.roleAdmin')}</option>
                </select>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('myTeam.inviteNote')}</p>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {t('common.cancel', 'Annuler')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors inline-flex items-center gap-2"
                >
                  {saving && <RefreshCw size={14} className="animate-spin" />}
                  {t('myTeam.inviteUser')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation retrait rôle admin */}
      {roleConfirm !== null && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="h-1.5 w-full bg-gradient-to-r from-orange-400 to-orange-600" />
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-orange-100">
                  <Shield className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  {t('myTeam.roleConfirmTitle')}
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                {t('myTeam.roleConfirmMessage', {
                  user:
                    [roleConfirm.user.firstname, roleConfirm.user.lastname]
                      .filter(Boolean)
                      .join(' ') || roleConfirm.user.email,
                })}
              </p>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={async () => {
                    const u = roleConfirm
                    setRoleConfirm(null)
                    await doRoleChange(u.user, u.newRole)
                  }}
                  className="flex-1 py-2.5 rounded-xl font-medium text-sm text-white bg-orange-500 hover:bg-orange-600 transition-colors"
                >
                  {t('common.confirm')}
                </button>
                <button
                  onClick={() => setRoleConfirm(null)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors font-medium"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Erreur dernier admin */}
      {roleError !== null && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="h-1.5 w-full bg-gradient-to-r from-red-400 to-red-600" />
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-red-100">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  {t('myTeam.lastAdminTitle')}
                </h3>
              </div>
              <p className="text-sm text-gray-600">{roleError}</p>
              <button
                onClick={() => setRoleError(null)}
                className="w-full py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors font-medium"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
